import { BREAK_OPTIONS } from '../../constants';

import editTimesheetGroupComponentUrl from './editTimesheetGroupComponent.html';

class EditTimesheetGroupCtrl {
  breaks = [];
  breakOptions = BREAK_OPTIONS;

  constructor(Authentication, Utils, TimesheetsUtils, sdk) {
    'ngInject';

    Object.assign(this, { Authentication, Utils, TimesheetsUtils, sdk });
    this.TIMESHEET_ENTRY_TYPES = sdk('constants').TIMESHEET_ENTRY_TYPES;
  }

  $onInit() {
    this.originalTimesheets = this.timesheets.map((t) => ({ ...t }));
    this.baseSequenceId = this.timesheets.length ? this.timesheets[0].baseSequenceId : null;

    let lastTimesheet = this.timesheets[this.timesheets.length - 1];
    if (!lastTimesheet) {
      // add dummy timesheet with empty clockIn and clockOut
      this.timesheets.push(
        (lastTimesheet = {
          type: this.TIMESHEET_ENTRY_TYPES.CLOCK_IN,
        })
      );
    }

    const clockIn = this.timesheets.find(this.TimesheetsUtils.isClockIn);
    const clockOut = this.timesheets.find(this.TimesheetsUtils.isClockOut);
    this.boundaryStart = clockIn?.timestamp ? new Date(clockIn.timestamp) : undefined;
    this.boundaryEnd = clockOut?.timestamp ? new Date(clockOut.timestamp) : undefined;

    this.isBreakRelevant = Boolean(this.Authentication.user.orgSettings.shiftBreakType);

    this.breaks = this._getBreaks();
    this.defaultDate = this.event?.dtstart || this.newTimesheet?.defaultDate;

    // bind outside
    this.getDataForUpdate = this.getDataToBulkUpdate.bind(this);
    this.isValid = () => this.areBreaksCorrect() && this.isClockOutCorrect();
  }

  areBreaksCorrect() {
    const entry = {
      breaks: this.breaks,
      boundaryStart: this.boundaryStart,
      boundaryEnd: this.boundaryEnd,
    };

    return this.TimesheetsUtils.areBreaksCorrect(entry);
  }

  $doCheck() {
    this.totalBreakBinding = this.totalBreakMinutes;
  }

  getDateToCompare(date) {
    return date ? date.getTime() : date;
  }

  get totalBreakMinutes() {
    const durations = this.breaks.map((b) => b.durationMinutes);
    return Math.floor(this.Utils.sum(durations));
  }

  removeBreak(breakObject) {
    this.breaks = this.breaks.filter((b) => b !== breakObject);
    if (breakObject.startTimesheet) {
      breakObject.startTimesheet.deleted = true;
    }
    if (breakObject.endTimesheet) {
      breakObject.endTimesheet.deleted = true;
    }

    if (this.form) {
      this.form.$setDirty();
    }
  }

  addBreak() {
    this.breaks.push(this._getBreakObject());

    if (this.form) {
      this.form.$setDirty();
    }
  }

  filterTimesheetsToBulkUpdate(timesheet) {
    if (timesheet.deleted) {
      return Boolean(timesheet.id);
    }

    if (timesheet.id) {
      const original = this.originalTimesheets.find((t) => t.id === timesheet.id);
      return (
        this.getDateToCompare(original.timestamp) !== this.getDateToCompare(timesheet.timestamp)
      );
    }

    return Boolean(timesheet.timestamp);
  }

  mapTimesheetToBulkUpdate(timesheet) {
    if (timesheet.deleted || !timesheet.timestamp) {
      return {
        id: timesheet.id,
        deleted: true,
      };
    }

    const timestamp = `${this.Utils.formatDate(timesheet.timestamp)}T${this.Utils.formatTime(
      timesheet.timestamp,
      false,
      true
    )}`;

    const mapped = {
      ...(timesheet.id ? { id: timesheet.id } : {}),
      type: timesheet.type,
      timestamp,
      baseSequenceId: this.baseSequenceId,
    };

    if (this.newTimesheet) {
      mapped.user = { id: this.newTimesheet.user.id };
      mapped.positionId = this.newTimesheet.position.id;
      mapped.locationId = this.newTimesheet.location.id;
    } else {
      mapped.event = { id: this.event.id };
      mapped.user = { id: this.event.user.id };
    }

    return mapped;
  }

  getDataToBulkUpdate() {
    const sortedBreaks = [...this.breaks].sort((a, b) => a.breakStart > b.breakStart);

    const timesheets = this.timesheets.map((t) => ({ ...t }));
    let clockOutTimesheet = timesheets.find(this.TimesheetsUtils.isClockOut);
    const timesheetsById = {};
    timesheets.forEach((timesheet) => {
      if (timesheet.id) {
        timesheetsById[timesheet.id] = timesheet;
      }
    });

    this.setClockInTime(timesheets);
    clockOutTimesheet = this.setClockOutTime(timesheets, clockOutTimesheet);

    sortedBreaks.forEach((breakObject, index) => {
      this.setBreakStartTime(timesheets, timesheetsById, breakObject);
      this.setBreakEndTime(
        timesheets,
        timesheetsById,
        breakObject,
        sortedBreaks,
        index,
        clockOutTimesheet
      );
    });

    return timesheets
      .filter((t) => this.filterTimesheetsToBulkUpdate(t))
      .map((t) => this.mapTimesheetToBulkUpdate(t));
  }

  setClockInTime(timesheets) {
    const clockIn = timesheets.find(this.TimesheetsUtils.isClockIn);
    clockIn.timestamp = this.boundaryStart;
    if (!this.boundaryStart) {
      clockIn.deleted = true;
    }
  }

  setClockOutTime(timesheets, clockOutTimesheet) {
    if (!this.boundaryEnd && clockOutTimesheet) {
      clockOutTimesheet.deleted = true;
    } else if (this.boundaryEnd && !clockOutTimesheet) {
      timesheets.push({
        type: this.TIMESHEET_ENTRY_TYPES.CLOCK_OUT,
        timestamp: this.boundaryEnd,
      });
      return timesheets[timesheets.length - 1];
    } else if (this.boundaryEnd) {
      clockOutTimesheet.timestamp = this.boundaryEnd;
    }

    return clockOutTimesheet;
  }

  setBreakStartTime(timesheets, timesheetsById, breakObject) {
    if (breakObject.startTimesheet) {
      timesheetsById[breakObject.startTimesheet.id].timestamp = breakObject.breakStart;
    } else {
      timesheets.push({
        type: this.TIMESHEET_ENTRY_TYPES.BREAK_START,
        timestamp: breakObject.breakStart,
      });
    }
  }

  setBreakEndTime(timesheets, timesheetsById, breakObject, sortedBreaks, index, lastTimesheet) {
    if (breakObject.endTimesheet) {
      timesheetsById[breakObject.endTimesheet.id].timestamp = breakObject.breakEnd;
    } else if (!this.isLastBrakeEndIsClockOut(breakObject, sortedBreaks, index, lastTimesheet)) {
      timesheets.push({
        type: this.TIMESHEET_ENTRY_TYPES.BREAK_END,
        timestamp: breakObject.breakEnd,
      });
    }
  }

  isLastBrakeEndIsClockOut(breakObject, sortedBreaks, index, lastTimesheet) {
    if (index < sortedBreaks.length - 1) {
      return false;
    }

    return (
      this.TimesheetsUtils.isClockOut(lastTimesheet) &&
      this.getDateToCompare(lastTimesheet.timestamp) === this.getDateToCompare(breakObject.breakEnd)
    );
  }

  isClockOutCorrect() {
    return this.TimesheetsUtils.isClockOutCorrect(this.boundaryEnd, this.boundaryStart);
  }

  isBreakEndRequired(breakObject) {
    const sortedBreaks = [...this.breaks].sort((a, b) => a.breakStart > b.breakStart);
    return this.TimesheetsUtils.isBreakEndRequired(this.boundaryEnd, sortedBreaks, breakObject);
  }

  _getBreakObject(start, end, startTimesheet, endTimesheet) {
    return {
      breakStart: start && new Date(start),
      breakEnd: end && new Date(end),
      startTimesheet,
      endTimesheet,
      get durationMinutes() {
        if (this.breakStart && this.breakEnd) {
          return Math.floor((this.breakEnd - this.breakStart) / 1000 / 60);
        }

        return 0;
      },
    };
  }

  _getBreaks() {
    let previousTimesheet;
    return [...this.timesheets, {}]
      .map((timesheet) => {
        let breakObject = null;

        if (previousTimesheet && this.TimesheetsUtils.isBreakStart(previousTimesheet)) {
          breakObject = this._getBreakObject(
            previousTimesheet.timestamp,
            timesheet?.timestamp,
            previousTimesheet,
            this.TimesheetsUtils.isBreakEnd(timesheet) ? timesheet : null
          );
        }

        previousTimesheet = timesheet;
        return breakObject;
      })
      .filter(Boolean);
  }
}

export default {
  bindings: {
    event: '<',
    timesheets: '<',
    remove: '&',
    form: '<?',
    isLast: '<',
    getDataForUpdate: '=',
    isValid: '=',
    boundaryStart: '=?startDate',
    boundaryEnd: '=?endDate',
    totalBreakBinding: '=?totalBreak',
    readonly: '<?',
    index: '<?',
    removingDisabled: '<?',
    newTimesheet: '<',
  },
  templateUrl: editTimesheetGroupComponentUrl,
  controllerAs: 'ctrl',
  controller: EditTimesheetGroupCtrl,
};
